
"use client"

import React from "react"
import Link from "next/link"
import { VariantProps, cva } from "class-variance-authority"
import { Menu, LogOut, ChevronLeft, HelpCircle, Rows3, LayoutGrid } from "lucide-react"
import { usePathname } from 'next/navigation';

import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { useMenuSide } from "@/contexts/user-layout-prefs-context"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { ScrollArea } from "@/components/ui/scroll-area";
import Image from "next/image";
import { AuthSidebarActions } from "../layout/auth-sidebar-actions"
import AnimatedLogo from "@/components/ui/animated-logo";
import { LanguageSwitcher } from "@/components/layout/language-switcher";
import { useHelp } from "@/contexts/help-context";
import { useBranding } from "@/contexts/branding-context";
import { useTranslations } from "next-intl";
import { useHoldDragSwap } from "@/hooks/use-hold-drag-swap"


const SIDEBAR_DISPLAY_MODE_COOKIE_NAME = "sidebar_display_mode"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SIDEBAR_WIDTH = "16rem"
const SIDEBAR_WIDTH_ICON = "50px" 

const SIDEBAR_KEYBOARD_SHORTCUT = "b"

type SidebarDisplayMode = 'full' | 'icon';
type SidebarMenuView = 'list' | 'grid';

type SidebarContext = {
  open: boolean 
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
  displayMode: SidebarDisplayMode
  setDisplayMode: (mode: SidebarDisplayMode) => void;
  setOpen: (open: boolean) => void;
  isDashboardExpanded: boolean;
  setDashboardExpanded: (expanded: boolean) => void;
  menuView: SidebarMenuView;
  setMenuView: (view: SidebarMenuView) => void;
}

const SidebarContext = React.createContext<SidebarContext | null>(null)

function useSidebar() {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }
  return context
}

const LinkComponent = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithRef<typeof Link>
>(({ ...props }, ref) => {
  return <Link ref={ref} {...props} />;
});
LinkComponent.displayName = "LinkComponent";


const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultDisplayMode?: SidebarDisplayMode
  }
>(
  (
    {
      defaultDisplayMode = 'full',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile()
    const [openMobileInternal, setOpenMobileInternal] = React.useState(false)
    const [openDesktopInternal, setOpenDesktopInternal] = React.useState(true);
    const [displayModeDesktop, setDisplayModeDesktopInternal] = React.useState<SidebarDisplayMode>(defaultDisplayMode);
    const [isDashboardExpanded, setDashboardExpanded] = React.useState(false);
    // Starts at the SSR-safe default ('grid') on both server and client's
    // first hydration render; the real persisted value is applied in the
    // effect below, after hydration. Reading localStorage inside this
    // initializer meant the server always rendered 'grid' while the client's
    // first paint could read a different saved value, mismatching the
    // classNames this drives throughout the sidebar (React error #418).
    const [menuView, setMenuView] = React.useState<SidebarMenuView>('grid');
    React.useEffect(() => {
      try {
        const saved = localStorage.getItem('bb-sidebar-menu-view') as SidebarMenuView | null;
        if (saved) setMenuView(saved);
      } catch { /* ignore */ }
    }, []);
    const pathname = usePathname();

    // Opening the menu slides the record crate away (Gordon 2026-11-03).
    const menuWasOpen = React.useRef(false);
    React.useEffect(() => {
      const menuOpen = isMobile ? openMobileInternal : displayModeDesktop !== 'icon';
      if (menuOpen && !menuWasOpen.current) window.dispatchEvent(new CustomEvent('bb-crates', { detail: { open: false } }));
      menuWasOpen.current = menuOpen;
    }, [isMobile, openMobileInternal, displayModeDesktop]);

    React.useEffect(() => {
      if (isMobile && openMobileInternal) {
        setOpenMobileInternal(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, isMobile]);

    React.useEffect(() => {
      if (!isMobile) {
        const cookieDisplayMode = document.cookie.split('; ').find(row => row.startsWith(`${SIDEBAR_DISPLAY_MODE_COOKIE_NAME}=`))?.split('=')[1] as SidebarDisplayMode;
        if (cookieDisplayMode) {
          setDisplayModeDesktopInternal(cookieDisplayMode);
        }
      }
    }, [isMobile]);

    const setOpen = React.useCallback((open: boolean) => {
       if (isMobile) {
         setOpenMobileInternal(open);
       } else {
         setOpenDesktopInternal(open);
       }
    }, [isMobile]);

    const setDisplayMode = React.useCallback((mode: SidebarDisplayMode) => {
      if (!isMobile) {
        setDisplayModeDesktopInternal(mode);
        document.cookie = `${SIDEBAR_DISPLAY_MODE_COOKIE_NAME}=${mode}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        // When going to icon mode, dashboard should close
        if (mode === 'icon') {
            setDashboardExpanded(false);
        }
      }
    }, [isMobile]);

    // Persist sidebar menu view (list / grid) across sessions
    const setMenuViewPersisted = React.useCallback((view: SidebarMenuView) => {
      setMenuView(view);
      try { localStorage.setItem('bb-sidebar-menu-view', view); } catch { /* ignore */ }
    }, []);

    const toggleSidebar = React.useCallback(() => {
      // All menu triggers (desktop logo, mobile button and keyboard shortcut)
      // dismiss expanded audio windows before changing the menu state.
      window.dispatchEvent(new Event('close-vu-popup'));
      window.dispatchEvent(new Event('close-player-sheet'));
      if (isMobile) {
        setOpenMobileInternal(o => !o);
      } else {
        if (displayModeDesktop === 'full') {
          setDisplayMode('icon');
        } else {
          setDisplayMode('full');
        }
      }
    }, [isMobile, displayModeDesktop, setDisplayMode]);

    // coπ voice: "open menu" / "close menu" / "menu" (toggle).
    React.useEffect(() => {
      const onMenu = (e: Event) => {
        const open = (e as CustomEvent<{ open?: boolean }>).detail?.open;
        if (open === undefined) { toggleSidebar(); return; }
        if (isMobile) setOpenMobileInternal(open);
        else setDisplayMode(open ? 'full' : 'icon');
      };
      window.addEventListener('bb-menu', onMenu);
      return () => window.removeEventListener('bb-menu', onMenu);
    }, [toggleSidebar, isMobile, setDisplayMode]);

    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      }
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);
    
    const contextValue = React.useMemo<SidebarContext>(() => ({
        open: isMobile ? openMobileInternal : openDesktopInternal,
        openMobile: openMobileInternal,
        setOpenMobile: setOpenMobileInternal,
        isMobile,
        toggleSidebar,
        displayMode: isMobile ? 'full' : displayModeDesktop,
        setDisplayMode,
        setOpen,
        isDashboardExpanded,
        setDashboardExpanded,
        menuView,
        setMenuView: setMenuViewPersisted,
      }), [openMobileInternal, openDesktopInternal, isMobile, toggleSidebar, displayModeDesktop, setDisplayMode, setOpen, isDashboardExpanded, menuView, setMenuViewPersisted]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            className={cn("group/sidebar", className)}
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = "SidebarProvider"

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, children, ...props }, ref) => {
    const { isMobile, openMobile, setOpenMobile, displayMode } = useSidebar();
    // Left- or right-handed layout (Settings → Layout): the menu slides in from,
    // and docks on, the side the user chose.
    const menuSide = useMenuSide();

    if (isMobile) {
      return (
        // modal={false}: a MODAL sheet makes Radix swallow every tap outside
        // it, so with the menu open the chat FAB's onClick never fired — taps
        // only dismissed the sheet and chat seemed broken/behind the menu
        // (Gordon). Non-modal, one tap on the chat button both closes the
        // menu (outside-tap dismiss below) AND opens the chat.
        <Sheet open={openMobile} onOpenChange={setOpenMobile} modal={false} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            className="w-full bg-sidebar p-0 gap-0 text-sidebar-foreground flex flex-col"
            side={menuSide}
            // The header's menu button closes the sheet; the default X sat on
            // top of the help button (Gordon 2026-10-31: menu clean-up).
            showCloseButton={false}
            // Non-modal sheets don't auto-dismiss on outside taps — restore it.
            // But a menu opened FROM the sheet (the language picker) renders
            // in a portal outside it: a tap or focus there is not "outside" —
            // it closed the whole menu on the first tap, so the search box
            // and the languages themselves couldn't be used (2026-10-31).
            onPointerDownOutside={(e) => {
              if ((e.target as Element | null)?.closest?.('[data-radix-popper-content-wrapper]')) { e.preventDefault(); return; }
              setOpenMobile(false)
            }}
            onInteractOutside={(e) => {
              if ((e.target as Element | null)?.closest?.('[data-radix-popper-content-wrapper]')) e.preventDefault()
            }}
          >
            <SheetTitle className="sr-only">Sidebar Menu</SheetTitle>
            {children}
          </SheetContent>
        </Sheet>
      )
    }

    const effectiveWidthClass = (displayMode === 'full') ? 'w-[var(--sidebar-width)]' : 'w-[var(--sidebar-width-icon)]';
    
    return (
      <aside
        ref={ref}
        data-sidebar="sidebar"
        className={cn(
          "hidden sm:flex flex-col h-screen bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-[width] duration-300 ease-in-out fixed left-0 top-0 z-50 menuright:left-auto menuright:right-0 menuright:border-r-0 menuright:border-l",
          effectiveWidthClass,
          className 
        )}
        {...props} 
      >
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-10" 
          style={{backgroundImage: 'url(https://placehold.co/800x1200.png)', backgroundBlendMode: 'multiply'}}
          data-ai-hint="abstract texture"
        ></div>
        <div className="relative z-10 flex flex-col h-full">
            {children}
        </div>
      </aside>
    )
  }
)
Sidebar.displayName = "Sidebar"

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="fixedIcon"
      // High-contrast outline so the menu button reads as clickable (Gordon).
      className={cn("bb-glass-btn group/menu relative size-[var(--header-height)] rounded-none border-2 border-foreground/80 hover:border-foreground p-0 m-0", className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <AnimatedLogo />
      <span aria-hidden="true" className="absolute inset-0 z-10 flex items-center justify-center bg-sidebar text-sidebar-foreground opacity-0 group-hover/menu:opacity-100 group-focus-visible/menu:opacity-100 transition-opacity">
        <Menu className="!size-6" />
      </span>
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
})
SidebarTrigger.displayName = "SidebarTrigger"

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
   const { isMobile, displayMode, toggleSidebar, menuView, setMenuView } = useSidebar();
   const { helpEnabled, toggleHelp } = useHelp();
   const t = useTranslations("nav");
   const common = useTranslations("common");
   const isIconOnly = !isMobile && displayMode === 'icon';
   // Hold and drag the menu button across the screen to swap sides.
   const swap = useHoldDragSwap();

    if (isMobile) {
      return (
        <div
          ref={ref}
          className={cn(
            // Mirrored (menu on the right): menu button top right, the rest left (Gordon 2026-10-31).
            "flex items-center border-b border-sidebar-border/50 h-[var(--header-height)] justify-between pr-2 bg-sidebar menuright:flex-row-reverse menuright:pr-0 menuright:pl-2",
            className
            )}
          {...props}
        >
          {/* While the menu is open its button is just the menu icon, not the
              artwork slideshow (Gordon 2026-10-31: "too busy"). Tap = close. */}
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={t("closeMenu")}
            className="flex size-[var(--header-height)] flex-shrink-0 items-center justify-center bg-sidebar text-sidebar-foreground border-2 border-foreground/80 hover:border-foreground"
          >
            <Menu className="!size-6" />
          </button>
           <div className="flex items-center gap-1 menuright:flex-row-reverse">
            {/* Language switcher — mobile only (hidden in main header on mobile) */}
            <LanguageSwitcher variant="compact" />
            {/* Help mode toggle — mobile only */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8 text-sidebar-foreground/80 hover:text-sidebar-foreground transition-colors",
                helpEnabled && "text-primary bg-primary/10"
              )}
              onClick={toggleHelp}
              aria-label={helpEnabled ? common('helpTurnOff') : common('helpTurnOn')}
              title={helpEnabled ? common('helpOnTitle') : common('helpOffTitle')}
            >
              <HelpCircle className={cn("h-4 w-4", helpEnabled && "text-primary")} />
            </Button>
          </div>
        </div>
      )
    }

    if (isIconOnly) {
      return (
        <div ref={ref} className={cn("flex items-center w-full p-0 h-[var(--header-height)] justify-start menuright:justify-end", className)} {...props}>
          <Button
            onClick={toggleSidebar}
            variant="ghost"
            className="bb-glass-btn group/menu relative flex items-center justify-center flex-shrink-0 size-[var(--header-height)] p-0 rounded-none border-2 border-foreground/80 hover:border-foreground"
            aria-label={t("openMenu")}
            {...swap}
          >
            <AnimatedLogo />
            <span aria-hidden="true" className="absolute inset-0 z-10 flex items-center justify-center bg-sidebar text-sidebar-foreground opacity-0 group-hover/menu:opacity-100 group-focus-visible/menu:opacity-100 transition-opacity">
              <Menu className="!size-6" />
            </span>
          </Button>
        </div>
      );
    }

    // Open and collapsed menus share the same header height as favourites.
    return (
      <div ref={ref} className={cn("w-full p-0", className)} {...props}>
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={t("closeMenu")}
          className="flex w-full h-[var(--header-height)] items-center justify-between overflow-hidden bg-sidebar text-sidebar-foreground border-b border-sidebar-border hover:bg-sidebar-accent transition-colors group menuright:flex-row-reverse"
          {...swap}
        >
          <span className="flex size-[var(--header-height)] shrink-0 items-center justify-center border-2 border-foreground/80"><Menu className="!size-6" /></span>
          <span className="pr-4 text-sm font-bold uppercase tracking-wider">{t("menu")}</span>
        </button>
        <div className="flex items-center justify-start gap-0.5 px-0.5 py-0.5 border-b border-sidebar-border/50 menuright:justify-end">
          {/* View toggle — rows (list) or grid (Gordon: "missing listing
              view button to rows instead of grids"). */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMenuView('list')}
            aria-pressed={menuView === 'list'}
            className={cn(
              "h-7 px-2 rounded-none border-2",
              menuView === 'list' ? "border-primary bg-primary/10 text-foreground" : "border-foreground/30 text-muted-foreground hover:border-foreground/60"
            )}
            title={common("listView")}
            aria-label={common("listView")}
          >
            <Rows3 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMenuView('grid')}
            aria-pressed={menuView === 'grid'}
            className={cn(
              "h-7 px-2 rounded-none border-2",
              menuView === 'grid' ? "border-primary bg-primary/10 text-foreground" : "border-foreground/30 text-muted-foreground hover:border-foreground/60"
            )}
            title={common("gridView")}
            aria-label={common("gridView")}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
})
SidebarHeader.displayName = "SidebarHeader"

interface SidebarContentProps extends React.ComponentProps<"div"> {}

const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
       <div
          ref={ref}
          className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto pb-[80px] sm:pb-[var(--player-height)]", className)}
          {...props}
        >
          {children}
        </div>
    );
  }
);
SidebarContent.displayName = "SidebarContent"

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => {
  const { displayMode, isMobile, menuView } = useSidebar();
  const isIconOnly = !isMobile && displayMode === 'icon';
  const isGridView = (!isIconOnly && menuView === 'grid') || (isMobile && menuView === 'grid');

  return (
    <ul
      ref={ref}
      className={cn(
        "w-full",
        isIconOnly && "flex flex-col p-0",
        !isIconOnly && !isGridView && "flex flex-col p-2 gap-1",
        // 4 tiles per row on desktop too — same rhythm as mobile, tiles
        // matching the 50px logo/favorites thumbnails (Gordon).
        isGridView && "grid grid-cols-4 p-0 gap-0 menuright:[direction:rtl]",
        className
      )}
      {...props}
    />
  )
})
SidebarMenu.displayName = "SidebarMenu"

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => {
    const { menuView, isMobile, displayMode } = useSidebar();
    const isIconOnly = !isMobile && displayMode === 'icon';
    const isGridView = (!isIconOnly && menuView === 'grid') || (isMobile && menuView === 'grid');
    return (
      <li
        ref={ref}
        className={cn(
          "w-full flex flex-col menuright:[direction:ltr]",
          isGridView && "aspect-square",
          className
        )}
        {...props}
      />
    );
});
SidebarMenuItem.displayName = "SidebarMenuItem"

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full h-full items-center overflow-hidden text-left outline-none ring-sidebar-ring transition-colors focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      },
      size: {
        default: "h-9 text-sm px-4 justify-start rounded-md gap-3 menuright:flex-row-reverse menuright:text-right",
        mobile: "h-12 text-base px-3 justify-start rounded-md gap-3 menuright:flex-row-reverse menuright:text-right",
        icon: "w-full h-[50px] justify-center",
        // Grid tiles: icon centered on top, small always-visible label below
        // (Gordon: "add menu grid labels back but make sure they don't break
        // the grid button… icon stays in center, label below"). The label may
        // wrap to a second line and then clamps; grid rows auto-grow so a
        // wrapped label never overflows the tile.
        grid: "relative flex-col gap-1 p-1.5 pt-2 justify-center items-center rounded-none [&>svg]:size-7 [&>svg]:shrink-0 [&>span:last-child]:w-full [&>span:last-child]:text-center [&>span:last-child]:text-[9px] [&>span:last-child]:leading-[1.15] [&>span:last-child]:break-words [&>span:last-child]:line-clamp-2",
        "mobile-grid": "relative flex-col gap-1 p-1.5 pt-2 justify-center items-center rounded-none [&>svg]:size-7 [&>svg]:shrink-0 [&>span:last-child]:w-full [&>span:last-child]:text-center [&>span:last-child]:text-[9px] [&>span:last-child]:leading-[1.15] [&>span:last-child]:break-words [&>span:last-child]:line-clamp-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

type SidebarMenuButtonProps = React.RefAttributes<HTMLAnchorElement | HTMLButtonElement> & {
  href?: string;
  isActive?: boolean;
  tooltip?: string | Omit<React.ComponentProps<typeof TooltipContent>, "children"> & { children?: React.ReactNode };
  children: React.ReactNode;
  variant?: VariantProps<typeof sidebarMenuButtonVariants>["variant"];
  size?: 'default' | 'mobile' | 'icon' | 'grid' | 'mobile-grid';
  className?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  disabled?: boolean;
} & Omit<React.ComponentPropsWithoutRef<"a">, "href" | "children" | "className" | "onClick" | "disabled">
  & Omit<React.ComponentPropsWithoutRef<"button">, "children" | "className" | "onClick" | "disabled">;

const SidebarMenuButton = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, SidebarMenuButtonProps>(
  (
    {
      href,
      isActive = false,
      variant = "default",
      tooltip,
      children,
      onClick, 
      disabled,
      size,
      className,
      ...restProps
    },
    ref
  ) => {
    const { isMobile, setOpenMobile, setDisplayMode } = useSidebar();
    
    const Comp = href ? LinkComponent : "button";

    const handleInternalClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (onClick) {
        onClick(event);
      }
      if (isMobile) {
        setOpenMobile(false);
      } else {
        setDisplayMode('icon');
      }
    };
    
    const { displayMode, menuView } = useSidebar();
    const isIconOnly = !isMobile && displayMode === 'icon';
    
    const finalSize = size ?? 
        (isIconOnly 
            ? "icon" 
            : (isMobile 
                ? (menuView === 'grid' ? 'mobile-grid' : 'mobile')
                : (menuView === 'grid' ? 'grid' : 'default')
            )
        );

    const commonProps: any = {
      className: cn(
        sidebarMenuButtonVariants({ variant, size: finalSize, className }),
        isActive && "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90"
      ),
      onClick: handleInternalClick,
      disabled: disabled,
      ...restProps,
    };

    if (Comp === LinkComponent) {
      commonProps.href = href!;
    } else {
       commonProps.type = "button";
    }
    
    const [iconElement, ...restChildren] = React.Children.toArray(children);

    const buttonElement = (
      <Comp ref={ref as any} {...commonProps}>
        {iconElement}
        <span className={cn(
          finalSize === 'icon' && 'sr-only',
          (finalSize === 'grid' || finalSize === 'mobile-grid')
            ? 'mt-0.5 leading-tight text-center w-full line-clamp-2'
            : 'truncate'
        )}>{restChildren}</span>
      </Comp>
    );
    
    const showTooltip = tooltip && isIconOnly;

    if (showTooltip) {
      const tooltipContentProps = typeof tooltip === 'string'
        ? { children: <span className="text-xs">{tooltip}</span> }
        : tooltip;

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {buttonElement}
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="center"
            sideOffset={10}
            {...tooltipContentProps}
            className={cn("bg-sidebar text-sidebar-foreground border-sidebar-border", typeof tooltip === 'object' && tooltip.className)}
          />
        </Tooltip>
      );
    }
    return buttonElement;
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";

export {
    Sidebar,
    SidebarContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarTrigger,
    useSidebar,
    sidebarMenuButtonVariants,
    type SidebarMenuButtonProps,
};
